import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import '../styles/ExamPage.css';

export default function ExamPage({ token }) {
  const { type } = useParams();
  const navigate = useNavigate();

  const examSubjectName = decodeURIComponent(type || 'Ingliz tili');
  const [questions, setQuestions] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flagged, setFlagged] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(0);
  const [certificateData, setCertificateData] = useState(null);
  const [showReview, setShowReview] = useState(false);

  // 30 minute countdown timer (1800 seconds)
  const [secondsLeft, setSecondsLeft] = useState(30 * 60);

  useEffect(() => {
    fetchQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  useEffect(() => {
    if (submitted || loading) return;
    const interval = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          handleFinalSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitted, loading, questions]);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`/api/exam/questions/${encodeURIComponent(examSubjectName)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setQuestions(res.data);
      setAnswers({});
      setCurrentIdx(0);
    } catch (err) {
      console.error('Questions fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOption = (key) => {
    setAnswers(prev => ({
      ...prev,
      [currentIdx]: key
    }));
  };

  const toggleFlag = (idx) => {
    setFlagged(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };

  const handleFinalSubmit = async () => {
    setShowConfirmModal(false);
    setSubmitting(true);

    let correctCount = 0;
    questions.forEach((q, idx) => {
      if (answers[idx] === q.correct_answer) {
        correctCount++;
      }
    });

    const calculatedScore = questions.length ? (correctCount / questions.length) * 100 : 0;
    setScore(calculatedScore);

    try {
      const res = await axios.post('/api/exam/submit-exam', {
        applicantId: 1,
        applicantName: 'Aliyev Anvarbek Anvarovich',
        examType: examSubjectName,
        answers,
        score: calculatedScore
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      setCertificateData(res.data);
      setSubmitted(true);
    } catch (err) {
      console.error('Submit error:', err);
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const formatTimer = (sec) => {
    const mins = Math.floor(sec / 60);
    const secs = sec % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="exam-loading-wrapper">
        <div className="exam-spinner"></div>
        <h2>Imtihon savollari yuklanmoqda...</h2>
        <p>Turon universiteti test tizimiga ulanmoqda</p>
      </div>
    );
  }

  // RESULT VIEW
  if (submitted) {
    const isPassed = score >= 60;
    return (
      <div className="exam-result-layout">
        <div className="container result-container">
          <div className="result-card-box">
            <div className={`result-header-banner ${isPassed ? 'passed' : 'failed'}`}>
              <div className="result-stamp-icon">{isPassed ? '🎓' : '⚠️'}</div>
              <h1 className="result-title-main">
                {isPassed ? 'Tabriklaymiz! Siz imtihondan oʻtdingiz!' : 'Afsuski, oʻtish bali toʻplanmadi'}
              </h1>
              <p className="result-sub-desc">
                {isPassed
                  ? 'Turon universiteti huzuridagi malakaviy imtihon sinovi natijasi tasdiqlandi.'
                  : 'Minimal oʻtish bali 60% qilib belgilangan. Qayta topshirish uchun ilmiy boʻlimga murojaat qiling.'}
              </p>
            </div>

            {/* Score circle & breakdown */}
            <div className="score-summary-grid">
              <div className="score-circle-panel">
                <div className={`score-ring ${isPassed ? 'passed' : 'failed'}`}>
                  <span className="score-percentage-num">{score.toFixed(1)}%</span>
                  <span className="score-grade-tag">{isPassed ? 'OʻTDI (PASSED)' : 'OʻTMADI'}</span>
                </div>
              </div>

              <div className="score-meta-panel">
                <div className="score-meta-row">
                  <span className="sm-label">Imtihon fani:</span>
                  <span className="sm-val">{examSubjectName}</span>
                </div>
                <div className="score-meta-row">
                  <span className="sm-label">Toʻgʻri javoblar:</span>
                  <span className="sm-val font-bold">
                    {questions.filter((q, idx) => answers[idx] === q.correct_answer).length} / {questions.length} ta
                  </span>
                </div>
                <div className="score-meta-row">
                  <span className="sm-label">Belgilangan oʻtish bali:</span>
                  <span className="sm-val">60.0%</span>
                </div>
                <div className="score-meta-row">
                  <span className="sm-label">Guvohnoma raqami:</span>
                  <span className="sm-val mono highlight">
                    {certificateData?.certificateNumber || 'PhD-TURON-2026-0041'}
                  </span>
                </div>
                <div className="score-meta-row">
                  <span className="sm-label">Sinov sanasi:</span>
                  <span className="sm-val">{new Date().toLocaleDateString('uz-UZ')}</span>
                </div>
              </div>
            </div>

            {/* Official Academic Certificate Preview if Passed */}
            {isPassed && (
              <div className="academic-certificate-preview" id="printable-certificate">
                <div className="cert-border-outer">
                  <div className="cert-border-inner">
                    <div className="cert-top-emblem">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#0f2b5c" strokeWidth="1.8">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 2l3 7h7l-5.5 4.5 2 7-6.5-4.5-6.5 4.5 2-7-5.5-4.5h7z" fill="#f59e0b" stroke="none" />
                      </svg>
                    </div>

                    <span className="cert-uni-name">TURON UNIVERSITETI</span>
                    <h2 className="cert-doc-title">MALAKAVIY IMTIHON GUVOHNOMASI</h2>
                    <span className="cert-number-line">№ {certificateData?.certificateNumber || 'PhD-TURON-2026-0041'}</span>

                    <p className="cert-body-paragraph">
                      Ushbu guvohnoma shuni tasdiqlaydiki, <strong>Aliyev Anvarbek Anvarovich</strong> haqiqatan ham
                      Turon universiteti Ilmiy kengashi huzuridagi imtihon komissiyasida{' '}
                      <strong>{examSubjectName}</strong> fani boʻyicha malakaviy imtihon sinovini
                      muvaffaqiyatli topshirdi va <strong>{score.toFixed(1)}%</strong> ball toʻpladi.
                    </p>

                    <div className="cert-bottom-signatures">
                      <div className="sign-col">
                        <span className="sign-role">Komissiya raisi:</span>
                        <div className="sign-signature-line">____________________</div>
                        <span className="sign-name">Prof. O.H. Xolmuhamedov</span>
                      </div>

                      <div className="cert-official-seal">
                        <div className="seal-round">
                          <span>TURON</span>
                          <small>MUHR</small>
                        </div>
                      </div>

                      <div className="sign-col">
                        <span className="sign-role">Ilmiy boʻlim boshligʻi:</span>
                        <div className="sign-signature-line">____________________</div>
                        <span className="sign-name">Dots. B.M. Ismoilov</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div className="result-actions-row">
              {isPassed && (
                <button className="btn-print-cert" onClick={() => window.print()}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 6 2 18 2 18 9"></polyline><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path><rect x="6" y="14" width="12" height="8"></rect></svg>
                  <span>Guvohnomani chop etish</span>
                </button>
              )}

              <button className="btn-toggle-review" onClick={() => setShowReview(!showReview)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
                <span>{showReview ? 'Javoblar tahlilini yopish' : 'Savollar va toʻgʻri javoblar tahlili'}</span>
              </button>

              <button className="btn-back-dashboard" onClick={() => navigate('/dashboard')}>
                <span>Shaxsiy kabinetga qaytish →</span>
              </button>
            </div>

            {/* Answer review section */}
            {showReview && (
              <div className="exam-answers-review-list">
                <h3>Savollar va javoblar tahlili:</h3>
                {questions.map((q, idx) => {
                  const userAnswer = answers[idx];
                  const isCorrect = userAnswer === q.correct_answer;
                  return (
                    <div key={q.id} className={`review-question-box ${isCorrect ? 'correct' : 'incorrect'}`}>
                      <div className="review-q-header">
                        <span className="review-q-num">Savol #{idx + 1}</span>
                        <span className={`review-q-status ${isCorrect ? 'correct' : 'incorrect'}`}>
                          {isCorrect ? '✓ Toʻgʻri' : '✕ Notoʻgʻri'}
                        </span>
                      </div>
                      <p className="review-q-stem">{q.question}</p>

                      <div className="review-options-list">
                        {Object.entries(q.options || {}).map(([key, val]) => (
                          <div
                            key={key}
                            className={`review-option-item ${
                              key === q.correct_answer ? 'is-correct' : (key === userAnswer ? 'user-wrong' : '')
                            }`}
                          >
                            <span className="ro-key">{key.toUpperCase()}</span>
                            <span className="ro-val">{val}</span>
                            {key === q.correct_answer && <span className="ro-tag green">Toʻgʻri javob</span>}
                            {key === userAnswer && key !== q.correct_answer && <span className="ro-tag red">Sizning javobingiz</span>}
                          </div>
                        ))}
                      </div>

                      {q.explanation && (
                        <div className="review-explanation-box">
                          <strong>Izoh:</strong> {q.explanation}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ACTIVE EXAM VIEW
  const currentQ = questions[currentIdx] || { question: '', options: {} };
  const totalQuestions = questions.length;
  const answeredCount = Object.keys(answers).length;
  const progressPercent = totalQuestions ? ((answeredCount) / totalQuestions) * 100 : 0;
  const isTimeCritical = secondsLeft <= 300; // < 5 mins

  return (
    <div className="exam-active-layout">
      {/* Top Test Navigation Bar */}
      <div className="exam-top-toolbar">
        <div className="container toolbar-content">
          <div className="exam-brand-info">
            <span className="exam-subject-badge">📖 {examSubjectName}</span>
            <span className="exam-applicant-name">Talabgor: <strong>Aliyev Anvarbek Anvarovich</strong></span>
          </div>

          <div className="exam-controls-right">
            <div className={`exam-timer-box ${isTimeCritical ? 'critical' : ''}`}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
              <span className="timer-digits">{formatTimer(secondsLeft)}</span>
            </div>

            <button className="btn-finish-exam-top" onClick={() => setShowConfirmModal(true)}>
              Imtihonni yakunlash ✓
            </button>
          </div>
        </div>
      </div>

      {/* Progress Line */}
      <div className="exam-progress-track">
        <div className="exam-progress-fill" style={{ width: `${progressPercent}%` }}></div>
      </div>

      <div className="container exam-body-container">
        {/* Main 2-Column Split: Question Panel (Left) & Navigator (Right) */}
        <div className="exam-workstation-grid">
          {/* LEFT: Current Question Card */}
          <div className="exam-question-column">
            <div className="question-card-wrapper">
              <div className="qc-header">
                <div className="qc-number-badge">
                  <span>Savol {currentIdx + 1} / {totalQuestions}</span>
                </div>
                <button
                  className={`btn-flag-question ${flagged[currentIdx] ? 'flagged' : ''}`}
                  onClick={() => toggleFlag(currentIdx)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill={flagged[currentIdx] ? '#f59e0b' : 'none'} stroke="currentColor" strokeWidth="2"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"></path><line x1="4" y1="22" x2="4" y2="15"></line></svg>
                  <span>{flagged[currentIdx] ? 'Belgilangan (Flagged)' : 'Koʻrib chiqish uchun belgilash'}</span>
                </button>
              </div>

              {/* Question Text */}
              <div className="qc-stem-box">
                <p className="qc-question-text">{currentQ.question}</p>
              </div>

              {/* Options List */}
              <div className="qc-options-stack">
                {Object.entries(currentQ.options || {}).map(([key, text]) => {
                  const isSelected = answers[currentIdx] === key;
                  return (
                    <div
                      key={key}
                      className={`qc-option-row ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleSelectOption(key)}
                    >
                      <div className="qc-option-radio">
                        <span className="radio-circle-custom">
                          {isSelected && <span className="radio-dot-inner"></span>}
                        </span>
                      </div>
                      <div className="qc-key-pill">{key.toUpperCase()}</div>
                      <div className="qc-option-text">{text}</div>
                    </div>
                  );
                })}
              </div>

              {/* Bottom Nav Buttons */}
              <div className="qc-bottom-buttons">
                <button
                  className="btn-qc-nav prev"
                  onClick={() => setCurrentIdx(prev => Math.max(0, prev - 1))}
                  disabled={currentIdx === 0}
                >
                  ← Oldingi
                </button>

                <div className="qc-answered-info">
                  Javob berildi: <strong>{answeredCount}</strong> / {totalQuestions}
                </div>

                {currentIdx < totalQuestions - 1 ? (
                  <button
                    className="btn-qc-nav next"
                    onClick={() => setCurrentIdx(prev => Math.min(totalQuestions - 1, prev + 1))}
                  >
                    Keyingi →
                  </button>
                ) : (
                  <button
                    className="btn-qc-nav finish"
                    onClick={() => setShowConfirmModal(true)}
                  >
                    Yakunlash ✓
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT: Question Navigator Panel */}
          <div className="exam-navigator-column">
            <div className="navigator-card">
              <h4 className="nav-title">Savollar navigatsiyasi</h4>

              <div className="nav-grid-buttons">
                {questions.map((_, idx) => {
                  const isCurrent = idx === currentIdx;
                  const isAnswered = answers[idx] !== undefined;
                  const isFlag = flagged[idx];
                  let statusClass = '';
                  if (isCurrent) statusClass = 'current';
                  else if (isFlag) statusClass = 'flagged';
                  else if (isAnswered) statusClass = 'answered';

                  return (
                    <button
                      key={idx}
                      className={`nav-grid-btn ${statusClass}`}
                      onClick={() => setCurrentIdx(idx)}
                    >
                      {idx + 1}
                      {isFlag && <span className="nav-flag-dot"></span>}
                    </button>
                  );
                })}
              </div>

              <div className="nav-legend-box">
                <div className="legend-item">
                  <span className="legend-sample answered"></span>
                  <span>Javob berilgan ({answeredCount})</span>
                </div>
                <div className="legend-item">
                  <span className="legend-sample flagged"></span>
                  <span>Belgilangan ({Object.values(flagged).filter(Boolean).length})</span>
                </div>
                <div className="legend-item">
                  <span className="legend-sample empty"></span>
                  <span>Javob berilmagan ({totalQuestions - answeredCount})</span>
                </div>
              </div>

              <div className="nav-rules-reminder">
                <p>
                  📌 Har bir savolga bitta javob tanlang. Imtihon yakunlangach natijangiz darhol hisoblanadi.
                </p>
              </div>

              <button
                className="btn-full-finish"
                onClick={() => setShowConfirmModal(true)}
              >
                Imtihonni topshirish
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="exam-modal-overlay">
          <div className="exam-modal-box">
            <div className="modal-warning-icon">⚠️</div>
            <h3>Imtihonni yakunlashga ishonchingiz komilmi?</h3>
            <p>
              Siz jami <strong>{totalQuestions}</strong> ta savoldan <strong>{answeredCount}</strong> tasiga javob berdingiz.
              {answeredCount < totalQuestions && (
                <span className="modal-unanswered-warn">
                  <br />Diqqat: {totalQuestions - answeredCount} ta savolga hali javob belgilanmagan!
                </span>
              )}
            </p>
            <div className="modal-actions-row">
              <button
                className="btn-modal-cancel"
                onClick={() => setShowConfirmModal(false)}
                disabled={submitting}
              >
                Savollarga qaytish
              </button>
              <button
                className="btn-modal-confirm"
                onClick={handleFinalSubmit}
                disabled={submitting}
              >
                {submitting ? 'Natija hisoblanmoqda...' : 'Ha, yakunlash'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
